import React, { memo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DashboardHeader from "./components/DashboardHeader";
import LazyTabs from "./components/LazyTabs";
import { DashboardProviders } from "./contexts/DashboardProviders";
import AppToaster from "@/components/common/AppToaster";
import ErrorBoundary from "@/components/ui-custom/ErrorBoundary";
import ErrorFallback from "./components/ErrorFallback";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import useOnboardingStatus from '@/hooks/useOnboardingStatus';
import OnboardingTour from '@/components/onboarding/OnboardingTour';

// Optimized background blurs for better performance - especially on mobile
const BackgroundEffects = memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
    <div className="absolute -top-40 -right-20 w-60 h-60 bg-primary/10 rounded-full filter blur-2xl opacity-20"></div>
    <div className="absolute top-60 -left-20 w-60 h-60 bg-pet-purple/20 rounded-full filter blur-2xl opacity-30"></div>
    <div className="absolute bottom-0 right-10 w-40 h-40 bg-pet-blue/20 rounded-full filter blur-2xl opacity-20"></div>
  </div>
));

// Memoized header for better performance
const MemoizedHeader = memo(DashboardHeader);

// Key for tracking if subscription success has been processed
const SUBSCRIPTION_SUCCESS_KEY = "petdoc_subscription_processed";

const Dashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const subscription = searchParams.get("subscription");
  const sessionRefresh = searchParams.get("session_refresh");
  const { refreshUser, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const {
    hasActiveSubscription,
    refetch: refetchSubscription,
    isLoading: isSubscriptionLoading,
  } = useSubscriptionStatus();
  const { showOnboarding, completeOnboarding } = useOnboardingStatus();

  // Check if we've already processed this subscription success
  const isSubscriptionAlreadyProcessed = React.useRef(
    typeof window !== "undefined" &&
      sessionStorage.getItem(SUBSCRIPTION_SUCCESS_KEY) === "true"
  );

  // Extra authentication check for direct navigation to dashboard
  useEffect(() => {
    if (!isLoading && !user) {
      console.log("User not authenticated, redirecting from dashboard");
      navigate("/login", { state: { from: "/dashboard" } });
    }
  }, [user, isLoading, navigate]);

  // Check if we have subscription parameters and handle them only once
  useEffect(() => {
    // Only process if we haven't done so already
    if (
      subscription === "success" &&
      sessionRefresh === "true" &&
      !isSubscriptionAlreadyProcessed.current
    ) {
      // Mark as processed to prevent repeated processing
      isSubscriptionAlreadyProcessed.current = true;
      sessionStorage.setItem(SUBSCRIPTION_SUCCESS_KEY, "true");

      console.log("Processing subscription success for the first time");

      // Handle the subscription success
      const handleSuccessfulSubscription = async () => {
        try {
          console.log("Refreshing user session after subscription");
          // Refresh the session
          const {
            data: { session },
          } = await supabase.auth.getSession();

          if (session) {
            await refreshUser();
            // Also refresh subscription status
            await refetchSubscription();

            // Show success message
            toast.success(
              "Your subscription was completed successfully! Welcome to premium!",
              {
                duration: 5000,
                id: "subscription-success",
              }
            );

            // Safely clear URL parameters without triggering a reload
            setTimeout(() => {
              // Use history API to replace state without navigation
              const newUrl = window.location.pathname;
              window.history.replaceState({}, "", newUrl);
            }, 500);
          }
        } catch (error) {
          console.error("Error refreshing session:", error);
        }
      };

      handleSuccessfulSubscription();
    }
  }, [subscription, sessionRefresh, refreshUser, refetchSubscription]);

  // Grace period for subscription updates to propagate
  const [hasGracePeriod, setHasGracePeriod] = React.useState(() => {
    // Check if we're in a subscription success flow
    return (
      (subscription === "success" && sessionRefresh === "true") ||
      isSubscriptionAlreadyProcessed.current
    );
  });

  // Set a timeout to end the grace period
  React.useEffect(() => {
    if (hasGracePeriod) {
      console.log(
        "In grace period after subscription, skipping redirect check"
      );
      const timer = setTimeout(() => {
        console.log("Grace period ended, normal redirect checks will resume");
        setHasGracePeriod(false);
        // Clear the subscription processed flag after grace period
        sessionStorage.removeItem(SUBSCRIPTION_SUCCESS_KEY);
      }, 10000); // Increase to 10 seconds for safety

      return () => {
        clearTimeout(timer);
      };
    }
  }, [hasGracePeriod]);

  // If user is loaded and doesn't have a subscription, redirect to pricing
  useEffect(() => {
    // Skip redirect during grace period to prevent loops
    if (hasGracePeriod) {
      return;
    }

    if (
      !isLoading &&
      user &&
      hasActiveSubscription === false &&
      !isSubscriptionLoading
    ) {
      console.log("User has no active subscription, redirecting to pricing");
      navigate("/pricing", { state: { from: "/dashboard" } });
    }
  }, [
    user,
    isLoading,
    hasActiveSubscription,
    navigate,
    isSubscriptionLoading,
    hasGracePeriod,
  ]);

  const handleError = (error: Error) => {
    // Error silently handled by error boundary
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <ErrorBoundary
          fallback={({ error, resetErrorBoundary }) => (
            <ErrorFallback
              error={error}
              resetErrorBoundary={resetErrorBoundary}
            />
          )}
          onError={handleError}
        >
          <DashboardProviders>
            <div className="relative isolate overflow-hidden">
              {/* Optimized background blurs */}
              <BackgroundEffects />

              <MemoizedHeader />
              <LazyTabs />
            </div>
          </DashboardProviders>
        </ErrorBoundary>
        <AppToaster position="top-center" />
        {showOnboarding && (
          <OnboardingTour 
            isOpen={showOnboarding} 
            onClose={completeOnboarding} 
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
